import type { WeekAvailability } from "@/utils/availabilityUtils";
import { useRoles } from "@/hooks/useRoles";
import { stashMakersEmployeeFilter } from "@/utils/makersDeepLink";
import { isoKey } from "@/utils/weekUtils";
import { Link } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { AvailabilityTooltip } from "./AvailabilityTooltip";
import type { OooByEmployee } from "@/contexts/OooContext";
import { displayedOooHours } from "@/utils/oooDisplay";

interface AvailabilityTimelineProps {
  weekData: WeekAvailability[];
  oooByEmployee: OooByEmployee;
  onEmployeeClick?: (employeeId: string) => void;
}

export function AvailabilityTimeline({
  weekData,
  oooByEmployee,
  onEmployeeClick,
}: AvailabilityTimelineProps) {
  const { roleColorStyle } = useRoles();
  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const highlightedIdRef = useRef<string | null>(null);
  const [tooltipChip, setTooltipChip] = useState<HTMLElement | null>(null);

  const handleBodyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  const setHighlight = useCallback((empId: string | null) => {
    const container = bodyRef.current;
    if (!container || highlightedIdRef.current === empId) return;

    // Clear previous highlights
    if (highlightedIdRef.current) {
      container.classList.remove("has-highlight");
      const prev = container.querySelectorAll(".availability-chip--highlighted");
      prev.forEach((el) => el.classList.remove("availability-chip--highlighted"));
    }

    highlightedIdRef.current = empId;

    // Apply new highlights
    if (empId) {
      container.classList.add("has-highlight");
      const matches = container.querySelectorAll(`[data-employee-id="${empId}"]`);
      matches.forEach((el) => el.classList.add("availability-chip--highlighted"));
    }
  }, []);

  const handlePointerOver = useCallback((e: React.PointerEvent) => {
    const chip = (e.target as HTMLElement).closest<HTMLElement>(".availability-chip");
    setHighlight(chip?.dataset.employeeId ?? null);
    setTooltipChip(chip ?? null);
  }, [setHighlight]);

  const handlePointerLeave = useCallback(() => {
    setHighlight(null);
    setTooltipChip(null);
  }, [setHighlight]);

  return (
    <div className="availability-timeline">
      <div className="availability-timeline__header" ref={headerRef}>
        <div
          className="availability-timeline__grid"
          style={{ gridTemplateColumns: `repeat(${weekData.length}, 110px)` }}
        >
          {weekData.map((wd) => (
            <div key={isoKey(wd.week)} className="availability-timeline__header-cell">
              {wd.week.label}
            </div>
          ))}
        </div>
      </div>
      <div
        className="availability-timeline__body"
        ref={bodyRef}
        onScroll={handleBodyScroll}
        onPointerOver={handlePointerOver}
        onPointerLeave={handlePointerLeave}
      >
        <div
          className="availability-timeline__grid"
          style={{ gridTemplateColumns: `repeat(${weekData.length}, 110px)` }}
        >
          {weekData.map((wd) => (
            <div key={isoKey(wd.week)} className="availability-timeline__column">
              <div className="availability-timeline__count">{wd.employees.length}</div>
              <div
                className="availability-timeline__bar"
              >
                {wd.employees.map((emp) => {
                  const oooHours = displayedOooHours(
                    oooByEmployee.get(emp.id)?.get(wd.week.start.getTime()) ?? 0,
                    emp.capacity,
                  );
                  const effectiveAvailableHours = Math.max(0, emp.availableHours - oooHours);
                  const tooltip = oooHours > 0
                    ? `${emp.name}: ${effectiveAvailableHours}h available (${oooHours}h OOO)`
                    : `${emp.name}: ${emp.availableHours}h available`;
                  // An OOO chip keeps the role's background tint but not its text
                  // color, which the CSS replaces with a readable one.
                  const { color: roleTextColor, ...roleBackground } = roleColorStyle(emp.roles[0]);
                  const colorStyle = oooHours > 0 ? roleBackground : { ...roleBackground, color: roleTextColor };
                  const className = `availability-chip${oooHours > 0 ? " availability-chip--ooo" : ""}`;

                  if (onEmployeeClick) {
                    return (
                      <button
                        key={emp.id}
                        type="button"
                        onClick={() => onEmployeeClick(emp.id)}
                        className={`${className} availability-chip--button`}
                        style={colorStyle}
                        data-employee-id={emp.id}
                        data-ooo-hours={oooHours > 0 ? oooHours : undefined}
                        data-tooltip={tooltip}
                        aria-label={`Assign ${tooltip}`}
                      >
                        {emp.name}
                      </button>
                    );
                  }

                  return (
                    <Link
                      key={emp.id}
                      to="/makers"
                      search={(currentSearch) => {
                        const preservedSearch: Record<string, string> = {};
                        for (const [key, value] of Object.entries(currentSearch)) {
                          if (key !== "makersTab" && value !== undefined) {
                            preservedSearch[key] = value;
                          }
                        }
                        return preservedSearch;
                      }}
                      onClick={() => stashMakersEmployeeFilter([emp.id])}
                      className={className}
                      style={colorStyle}
                      data-employee-id={emp.id}
                      data-ooo-hours={oooHours > 0 ? oooHours : undefined}
                      data-tooltip={tooltip}
                      aria-label={tooltip}
                    >
                      {emp.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <AvailabilityTooltip targetEl={tooltipChip} />
    </div>
  );
}
